import logger from 'js/app/loggerSingleton';

import type {
  EnterpriseReportSettings,
  ExportFormat,
  LearnerActivityDateRange,
  ReportTypeEnum,
} from '@coursera/event-pulse-types';

import type { WeekDay } from 'bundles/enterprise-admin-analytics/components/WeekDaySelectField';
import {
  REPORT_POLLING_CONFIG,
  REPORT_TYPE_FOR_TRACKING,
} from 'bundles/enterprise-admin-analytics/constants/reportsv2';
import {
  DATE_RANGE_FOR_TRACKING,
  type ReportTrackingDataParams,
  type ReportType,
  type ReportV2,
} from 'bundles/enterprise-admin-analytics/types';

import _t from 'i18n!nls/enterprise-admin-analytics';

type ProgramEntry = { id: string; endedAt?: string | null };
type ContractEntry = { id: string; programIds?: string[] | null };

export const filterProgramsByContracts = <T extends ProgramEntry>(
  allPrograms: T[],
  contracts: ContractEntry[],
  selectedContractIds: string[]
): T[] => {
  if (!selectedContractIds.length || selectedContractIds.length === contracts.length) {
    return allPrograms;
  }
  const programIds = contracts
    .filter((contract) => selectedContractIds.includes(contract.id))
    .flatMap((contract) => contract.programIds ?? []);
  // Sparse foundation linkage: active contracts frequently carry no programIds, so the selected
  // contracts can derive an empty program list. Filtering to [] would wrongly empty the Programs
  // options and disable Generate/Schedule, so fall back to all programs when we can't narrow.
  if (programIds.length === 0) {
    return allPrograms;
  }
  return allPrograms.filter((program) => programIds.includes(program.id));
};

export const sortPrograms = <T extends ProgramEntry>(programs: T[]): T[] => {
  return programs.sort((a, b) => {
    if (a.endedAt !== b.endedAt) {
      return a.endedAt == null ? -1 : 1;
    }
    return 0;
  });
};

export const getPollingConfig = (reportType: ReportType) => {
  // Default polling configuration for fallback
  const DEFAULT_POLLING_FREQUENCY = 30 * 1000; // 30 seconds
  const DEFAULT_TIMEOUT = 4 * 60 * 60 * 1000; // 4 hours

  return (
    REPORT_POLLING_CONFIG[reportType as keyof typeof REPORT_POLLING_CONFIG] || {
      frequency: DEFAULT_POLLING_FREQUENCY,
      timeout: DEFAULT_TIMEOUT,
    }
  );
};

export const getScheduleDay = (schedule?: ReportV2['schedule']): WeekDay | null => {
  const scheduleDay = Object.keys(schedule ?? {})
    .filter((day) => day !== '__typename')
    .find((day) => schedule?.[day as keyof typeof schedule]);
  if (!scheduleDay) return null;
  return scheduleDay as WeekDay;
};

export const getScheduleStatusText = (schedule?: ReportV2['schedule']): string => {
  const scheduleDay = getScheduleDay(schedule);
  if (!scheduleDay) return '--';
  const capitalizedDay = scheduleDay.charAt(0).toUpperCase() + scheduleDay.slice(1);
  return _t('Scheduled for every #{day}', { day: capitalizedDay });
};

export const formatSelectionForTracking = (selectedCount: number, totalCount: number, label: string): string => {
  if (selectedCount > 0 && selectedCount === totalCount) {
    return `all_${label}`;
  }
  return `${selectedCount}_${label}`;
};

/**
 * Triggers a file download by creating a temporary anchor and programmatically clicking it.
 * Uses the presigned URL to download the report without opening a new tab.
 *
 * Sets the `download` attribute on the anchor so the browser treats the request as a file
 * download rather than navigation. That reduces cases where Microsoft Edge routes spreadsheet
 * URLs through Office Online (`view.officeapps.live.com`). See the `download` attribute on
 * https://developer.mozilla.org/en-US/docs/Web/HTML/Reference/Elements/a#download
 * For cross-origin URLs, `Content-Disposition` from the server still takes precedence when present.
 *
 * @param fileUrl Presigned or direct URL to the file
 * @param filename Optional default filename; omit to let the browser use the server-provided name
 */
export const downloadLink = (fileUrl: string, filename?: string): void => {
  const link = document.createElement('a');
  link.href = fileUrl;
  if (filename !== undefined) {
    link.download = filename;
  } else {
    link.setAttribute('download', '');
  }
  link.rel = 'noopener noreferrer';
  link.style.display = 'none';
  document.body.appendChild(link);
  try {
    link.click();

    // Remove the link after a delay to ensure browser has time to initiate download
    setTimeout(() => {
      if (link.parentNode) {
        document.body.removeChild(link);
      }
    }, 500);
  } catch (error) {
    logger.error('Error downloading link:', error);
    if (link.parentNode) {
      document.body.removeChild(link);
    }
  }
};

const getLearnerStatus = (configValues: ReportTrackingDataParams['configValues']) => {
  const status: ('invited' | 'joined' | 'removed')[] = [];
  if (configValues.includeInvitedLearners) status.push('invited');
  if (configValues.includeJoinedLearners) status.push('joined');
  if (configValues.includeRemovedMembers) status.push('removed');
  return status;
};

export const getReportTrackingData = ({
  reportType,
  configValues,
  totalContracts,
  totalPrograms,
  totalSkills,
}: ReportTrackingDataParams) => {
  const isCurriculum = reportType === 'CURRICULUM';
  const isSkillsReport = reportType === 'LEARNER_SKILLS';

  const enterpriseReportSettings: EnterpriseReportSettings = {
    reportType: REPORT_TYPE_FOR_TRACKING[reportType] as ReportTypeEnum,
    contracts: [formatSelectionForTracking(configValues.contractIds?.length ?? 0, totalContracts, 'contracts')],
    programs: [formatSelectionForTracking(configValues.programIds?.length ?? 0, totalPrograms, 'programs')],
    downloadOneCsvPerProgram: configValues.oneCsvPerProgram,
    exportFormat: configValues.exportFormat?.toLowerCase() as ExportFormat,
  };

  if (isCurriculum) {
    return { enterpriseReportSettings };
  }

  if (isSkillsReport) {
    enterpriseReportSettings.skills = [
      formatSelectionForTracking(configValues.skillIds?.length ?? 0, totalSkills, 'skills'),
    ];
  }

  enterpriseReportSettings.learnerActivityDateRange =
    (DATE_RANGE_FOR_TRACKING[configValues.dateRange.type] as LearnerActivityDateRange) || 'thirty_days';
  enterpriseReportSettings.learnerStatus = getLearnerStatus(configValues);

  return { enterpriseReportSettings };
};
